/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {Compiler, ComponentFactoryResolver, Injector, NgModuleFactory, NgModuleFactoryLoader, OpaqueToken} from '@angular/core';
import {Observable} from 'rxjs/Observable';
import {fromPromise} from 'rxjs/observable/fromPromise';
import {of } from 'rxjs/observable/of';
import {map} from 'rxjs/operator/map';
import {mergeMap} from 'rxjs/operator/mergeMap';

import {LoadChildren, Route} from './config';
import {flatten, wrapIntoObservable} from './utils/collection';

/**
 * @experimental
 */
export const /** @type {?} */ ROUTES = new OpaqueToken('ROUTES');
export class LoadedRouterConfig {
/**
 * @param {?} routes
 * @param {?} injector
 * @param {?} factoryResolver
 * @param {?} injectorFactory
 */
constructor(
public routes: Route[],
public injector: Injector,
public factoryResolver: ComponentFactoryResolver,
public injectorFactory: Function) {}
}

function LoadedRouterConfig_tsickle_Closure_declarations() {
/** @type {?} */
LoadedRouterConfig.prototype.routes;
/** @type {?} */
LoadedRouterConfig.prototype.injector;
/** @type {?} */
LoadedRouterConfig.prototype.factoryResolver;
/** @type {?} */
LoadedRouterConfig.prototype.injectorFactory;
}

export class RouterConfigLoader {
/**
 * @param {?} loader
 * @param {?} compiler
 */
constructor(private loader: NgModuleFactoryLoader,
private compiler: Compiler) {}
/**
 * @param {?} parentInjector
 * @param {?} loadChildren
 * @return {?}
 */
load(parentInjector: Injector, loadChildren: LoadChildren): Observable<LoadedRouterConfig> {
    return map.call(this.loadModuleFactory(loadChildren), (r: NgModuleFactory<any>) => {
      const /** @type {?} */ ref = r.create(parentInjector);
      const /** @type {?} */ injectorFactory = (parent: Injector) => r.create(parent).injector;
      return new LoadedRouterConfig(
          flatten(ref.injector.get(ROUTES)), ref.injector, ref.componentFactoryResolver,
          injectorFactory);
    });
  }
/**
 * @param {?} loadChildren
 * @return {?}
 */
private loadModuleFactory(loadChildren: LoadChildren): Observable<NgModuleFactory<any>> {
    if (typeof loadChildren === 'string') {
      return fromPromise(this.loader.load(loadChildren));
    } else {
      return mergeMap.call(wrapIntoObservable(loadChildren()), (t: NgModuleFactory<any>| any) => {
        if (t instanceof NgModuleFactory) {
          return of (t);
        } else {
          return fromPromise(this.compiler.compileModuleAsync(t));
        }
      });
    }
  }
}

function RouterConfigLoader_tsickle_Closure_declarations() {
/** @type {?} */
RouterConfigLoader.prototype.loader;
/** @type {?} */
RouterConfigLoader.prototype.compiler;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
